import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Input,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  ListIcon,
  useToast,
} from '@chakra-ui/react';
import {
  ViewIcon,
  ViewOffIcon,
  CheckCircleIcon,
  WarningIcon,
} from '@chakra-ui/icons';
import { changePassword } from '../api/auth';
import { validatePassword, PASSWORD_RULE_TEXT } from '../utils/passwordPolicy';
import { getApiErrorMessage } from '../utils/apiError';

const ChangePasswordModal = ({ isOpen, onClose, userId, userName }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      // Modal açıldığında şifre alanlarını temizle
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [isOpen]);

  const passwordCheck = validatePassword(newPassword);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!passwordCheck.isValid) {
      toast({
        title: 'Error',
        description: PASSWORD_RULE_TEXT,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await changePassword(userId, currentPassword, newPassword);
      toast({
        title: 'Success',
        description: 'Password changed successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: 'Error',
        description: getApiErrorMessage(error, 'Failed to change password.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change Password for {userName}</ModalHeader>
        <ModalBody>
          <FormControl mb={3}>
            <FormLabel>Current Password:</FormLabel>
            <InputGroup>
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
              <InputRightElement>
                <Button
                  size="sm"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <ViewOffIcon /> : <ViewIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>New Password:</FormLabel>
            <InputGroup>
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
              <InputRightElement>
                <Button
                  size="sm"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <ViewOffIcon /> : <ViewIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>

            <List spacing={1} mt={2} fontSize="sm">
              {passwordCheck.rules.map(rule => (
                <ListItem
                  key={rule.key}
                  color={rule.passed ? 'green.600' : 'gray.500'}
                >
                  <ListIcon
                    as={rule.passed ? CheckCircleIcon : WarningIcon}
                    color={rule.passed ? 'green.500' : 'gray.400'}
                  />
                  {rule.label}
                </ListItem>
              ))}
            </List>
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Confirm New Password:</FormLabel>
            <InputGroup>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
              <InputRightElement>
                <Button
                  size="sm"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleChangePassword}>
            Change Password
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangePasswordModal;
